import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Composant Error Boundary pour capturer les erreurs React
 * Affiche une interface de récupération d'erreur conviviale
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Erreur capturée par ErrorBoundary:", error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    // Nettoyer le localStorage et sessionStorage
    localStorage.removeItem("transmoove-language");
    sessionStorage.removeItem("transmoove-furniture-data");

    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-transmoove-bg flex items-center justify-center p-4">
          <Card className="max-w-md w-full border-transmoove-bar">
            <CardHeader className="bg-transmoove-bgDark">
              <CardTitle className="text-white text-xl">
                Oops ! Une erreur s'est produite
              </CardTitle>
              <CardDescription className="text-gray-100">
                L'application a rencontré un problème inattendu.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {this.state.error && (
                <div className="text-sm text-transmoove-text bg-gray-100 p-3 rounded">
                  <strong>Détails de l'erreur :</strong>
                  <br />
                  {this.state.error.message}
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  onClick={this.handleReload}
                  className="flex-1 bg-transmoove-bgDark hover:bg-transmoove-bg text-white"
                >
                  Recharger la page
                </Button>
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                  className="flex-1 border-transmoove-bar text-transmoove-text hover:bg-transmoove-bgLight"
                >
                  Réinitialiser
                </Button>
              </div>

              <div className="text-center text-sm text-transmoove-text">
                Si le problème persiste, contactez{" "}
                <a
                  href="https://www.transmoove.org/fr/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-transmoove-bgDark hover:text-transmoove-bar underline font-semibold"
                >
                  le support Transmoove
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
